import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { dashIfEmpty, formatDate } from "../lib/format";
import type { OrderDetail } from "../lib/fulfillmentOrders";
import { lockReasonLabel } from "../lib/lockReasons";
import { KycStatusBadge } from "./KycStatusBadge";
import { TrustTierBadge } from "./TrustTierBadge";

/**
 * Who placed the order, without opening the customer page: Retool TCG Fulfill Order's account
 * panel. The server sends an allowlist (`getOrderCustomer`); the lock note, staff notes and SEON
 * verdicts stay on the customer page. Whether the account is locked comes from the order's row,
 * as the header and the actions read it. On a tenant-owned order these are Loot OS's copy of the
 * account, and the tenant's own account (boxed's lock, say) may say otherwise.
 */
export function OrderCustomerPanel({
  order,
  customer,
  tenantOwned,
}: {
  order: OrderDetail["order"];
  customer: OrderDetail["customer"];
  tenantOwned: boolean;
}) {
  return (
    <section className="flex flex-col gap-1 text-sm lg:col-span-6">
      <Link
        className="font-medium underline underline-offset-4"
        to="/tenants/$tenantId/users/$userId"
        params={{ tenantId: order.tenantId, userId: order.endUserId }}
      >
        Account Manager
      </Link>
      <CustomerField label="Account ID">
        <span className="font-mono text-xs">{customer.externalId}</span>
      </CustomerField>
      <CustomerField label="Username">{dashIfEmpty(customer.username)}</CustomerField>
      <CustomerField label="Email">{dashIfEmpty(customer.email)}</CustomerField>
      <CustomerField label="Member since">{formatDate(customer.createdAt)}</CustomerField>
      <CustomerField label="Trust tier">
        <TrustTierBadge tier={customer.trustTier} />
      </CustomerField>
      <CustomerField label="KYC">
        {customer.kycStatus || customer.kycVerified ? (
          <KycStatusBadge status={customer.kycStatus} verified={customer.kycVerified} />
        ) : (
          "—"
        )}
      </CustomerField>
      {order.accountLocked ? (
        <p className="font-bold text-destructive">
          ACCOUNT IS LOCKED!
          {customer.lockReasonCode ? ` (${lockReasonLabel(customer.lockReasonCode)})` : null}
        </p>
      ) : null}
      {tenantOwned ? (
        <p className="text-xs text-muted-foreground">
          Loot OS's copy of the account. The tenant's own account may differ.
        </p>
      ) : null}
      <CustomerField label="Tenant reference">
        <span className="font-mono text-xs">{dashIfEmpty(order.tenantReferenceId)}</span>
      </CustomerField>
      <p className="text-muted-foreground">
        Processing {formatDate(order.processingAt)} · Shipped {formatDate(order.shippedAt)} ·
        Updated {formatDate(order.updatedAt)}
      </p>
    </section>
  );
}

function CustomerField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="flex flex-wrap items-center gap-1">
      {label}: {children}
    </p>
  );
}
